import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

/** Used until `BackofficeTimezoneSync` applies the account `timezone` setting. */
const DEFAULT_BACKOFFICE_DISPLAY_TIMEZONE = "Europe/London";

let backofficeDisplayTimezone = DEFAULT_BACKOFFICE_DISPLAY_TIMEZONE;

/**
 * IANA timezone from account settings (`timezone`), applied on app boot via `BackofficeTimezoneSync`.
 */
export function getBackofficeDisplayTimezone(): string {
  return backofficeDisplayTimezone;
}

export function setBackofficeDisplayTimezone(iana: string): void {
  const t = iana?.trim();
  if (t) {
    backofficeDisplayTimezone = t;
  }
}

/** Today's calendar day (YYYY-MM-DD) in the backoffice display timezone. */
export function todayInBackofficeTz(): string {
  return dayjs().tz(getBackofficeDisplayTimezone()).format("YYYY-MM-DD");
}

/**
 * Start of a calendar day (YYYY-MM-DD) in the backoffice display timezone, so
 * a date filter covers the same day the tables show, whatever the browser's
 * timezone.
 */
export function startOfDayInBackofficeTz(date: string): Dayjs {
  return dayjs.tz(`${date}T00:00:00`, getBackofficeDisplayTimezone());
}

/** End (23:59:59.999) of a calendar day in the backoffice display timezone. */
export function endOfDayInBackofficeTz(date: string): Dayjs {
  return dayjs.tz(`${date}T23:59:59.999`, getBackofficeDisplayTimezone());
}

function isIsoDateOrInstant(value: string): boolean {
  // Accept:
  // - date-only: 2026-04-23
  // - ISO instant: 2026-04-23T10:20:30Z / with offset
  return /^\d{4}-\d{2}-\d{2}(?:[T\s].*)?$/.test(value);
}

/**
 * Convert an instant (ISO / Date / unix ms / Dayjs) to the account display timezone for backoffice UI.
 */
export function toDayjsForBackofficeDisplay(
  date: Date | Dayjs | string | number | null | undefined,
): Dayjs | null {
  if (date === null || date === undefined || date === "") return null;

  const tz = getBackofficeDisplayTimezone();

  if (typeof date === "number") {
    const ms = date < 1e12 ? date * 1000 : date;
    const d = dayjs.utc(ms);
    return d.isValid() ? d.tz(tz) : null;
  }

  if (dayjs.isDayjs(date)) {
    const d = dayjs.utc(date.valueOf());
    return d.isValid() ? d.tz(tz) : null;
  }

  if (date instanceof Date) {
    const d = dayjs.utc(date);
    return d.isValid() ? d.tz(tz) : null;
  }

  const raw = String(date).trim();

  // IMPORTANT:
  // If backend already formatted a value (e.g. "18/04/2026 10:00") in a given timezone,
  // frontend must not "convert again" because that would shift the wall time.
  // We only convert ISO/date-like inputs which represent an actual instant/date.
  if (!isIsoDateOrInstant(raw)) return null;

  const d = dayjs.utc(raw);
  return d.isValid() ? d.tz(tz) : null;
}

/** @deprecated Prefer `toDayjsForBackofficeDisplay` */
export const toDayjsInLondon = toDayjsForBackofficeDisplay;

export function formatInBackofficeLondon(
  date: Date | Dayjs | string | number | null | undefined,
  formatPattern: string,
): string {
  if (typeof date === "string") {
    const raw = date.trim();
    if (raw && !isIsoDateOrInstant(raw)) {
      // Already formatted by backend (or any non-ISO string) → don't convert again.
      return raw;
    }
  }
  const d = toDayjsForBackofficeDisplay(date);
  return d ? d.format(formatPattern) : "";
}

/**
 * Get locale-aware date format based on country code
 * @param countryCode - ISO country code (e.g., "US", "GB", "PE")
 * @returns Date format string for dayjs
 */
export const getDateFormatByCountry = (countryCode: string): string => {
  const formats: Record<string, string> = {
    // Month/Day/Year format (USA, Philippines, Canada)
    US: "MM/DD/YYYY",
    PH: "MM/DD/YYYY",
    CA: "MM/DD/YYYY",

    // Day/Month/Year format (Most of Europe, Latin America, Asia)
    GB: "DD/MM/YYYY", // United Kingdom
    PE: "DD/MM/YYYY", // Peru (Lima timezone)
    ES: "DD/MM/YYYY", // Spain (Madrid timezone)
    FR: "DD/MM/YYYY", // France
    DE: "DD.MM.YYYY", // Germany
    IT: "DD/MM/YYYY", // Italy
    BR: "DD/MM/YYYY", // Brazil
    MX: "DD/MM/YYYY", // Mexico
    AR: "DD/MM/YYYY", // Argentina
    IN: "DD/MM/YYYY", // India
    AU: "DD/MM/YYYY", // Australia
    NZ: "DD/MM/YYYY", // New Zealand

    // Year-Month-Day format (China, Japan, Korea)
    CN: "YYYY-MM-DD",
    JP: "YYYY/MM/DD",
    KR: "YYYY-MM-DD",
  };

  return formats[countryCode?.toUpperCase()] || "DD/MM/YYYY"; // Default to DD/MM/YYYY
};

/**
 * Format a date for display according to country settings
 * @param date - Date to format (Date object, Dayjs, or ISO string)
 * @param countryCode - ISO country code
 * @returns Formatted date string (e.g., "02/18/2026" for US, "18/02/2026" for GB)
 */
export const formatDateForDisplay = (
  date: Date | Dayjs | string | null | undefined,
  countryCode: string = "GB", // Default to UK format
): string => {
  if (!date) return "";

  const inTz = toDayjsForBackofficeDisplay(date);
  if (!inTz) return "";

  const format = getDateFormatByCountry(countryCode);
  return inTz.format(format);
};

/**
 * Format a date with time for display according to country settings
 * @param date - Date to format
 * @param countryCode - ISO country code
 * @returns Formatted datetime string (e.g., "02/18/2026 3:30 PM" for US)
 */
export const formatDateTimeForDisplay = (
  date: Date | Dayjs | string | null | undefined,
  countryCode: string = "GB",
): string => {
  if (!date) return "";

  const inTz = toDayjsForBackofficeDisplay(date);
  if (!inTz) return "";

  const dateFormat = getDateFormatByCountry(countryCode);
  return inTz.format(`${dateFormat} hh:mm A`);
};

export const addOrdinalSuffixToDay = (date: Dayjs) => {
  const day = date.date();
  if (day > 3 && day < 21) return `${day}th`;
  switch (day % 10) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
};

export const formatMessageTime = (
  timestamp: number,
  format: string = "hh:mm A",
): string => {
  return formatInBackofficeLondon(timestamp, format);
};

export const formatDayHeader = (timestamp: number) => {
  return formatInBackofficeLondon(timestamp, "D MMM, YYYY");
};

export const normalizeTimestamp = (
  timestamp: number | Date | undefined,
): number => {
  if (!timestamp) {
    return 0;
  }
  if (timestamp instanceof Date) {
    return timestamp.getTime();
  }
  return Math.round(timestamp * 1000);
};

export const isNewDay = (
  currentTimestamp: number,
  previousTimestamp?: number,
) => {
  const currentDate = toDayjsForBackofficeDisplay(
    currentTimestamp < 1e12 ? currentTimestamp * 1000 : currentTimestamp,
  )?.startOf("day");
  const previousDate = previousTimestamp
    ? toDayjsForBackofficeDisplay(
        previousTimestamp < 1e12 ? previousTimestamp * 1000 : previousTimestamp,
      )?.startOf("day")
    : null;

  return (
    !previousDate || !currentDate || !currentDate.isSame(previousDate, "day")
  );
};

export const formatDateSeparator = (timestamp: number) => {
  const ms = timestamp < 1e12 ? timestamp * 1000 : timestamp;
  const date = toDayjsForBackofficeDisplay(ms);
  if (!date) return "";
  return `${date.format("MMMM")} ${addOrdinalSuffixToDay(date)}, ${date.year()}`;
};

/**
 * Convert a date to ISO 8601 date-only format (YYYY-MM-DD)
 *
 * **IMPORTANT**: This is for API/storage purposes ONLY!
 * - Use this when sending dates to the backend
 * - Use this when storing dates in forms
 * - DO NOT use this for display to users
 *
 * For displaying dates to users, use `formatDateForDisplay()` instead,
 * which respects country-specific date formats.
 *
 * @param date - Date to convert (Dayjs object or ISO string)
 * @returns ISO date string (YYYY-MM-DD) or null if invalid
 *
 * @example
 * // For API/storage:
 * getDateOnly(dayjs("2026-02-18")) // → "2026-02-18"
 *
 * // For display to user (use this instead):
 * formatDateForDisplay(dayjs("2026-02-18"), "US") // → "02/18/2026"
 * formatDateForDisplay(dayjs("2026-02-18"), "GB") // → "18/02/2026"
 */
export const getDateOnly = (
  date: dayjs.Dayjs | string | null | undefined,
): string | null => {
  if (!date) return null;

  // If it's already a Dayjs object, format it directly
  if (dayjs.isDayjs(date)) {
    return date.isValid() ? date.format("YYYY-MM-DD") : null;
  }

  // If it's a string, parse it in UTC to avoid timezone shifts
  // This ensures "2025-07-17" stays as "2025-07-17" regardless of local timezone
  const dayjsDate = dayjs.utc(date);
  return dayjsDate.isValid() ? dayjsDate.format("YYYY-MM-DD") : null;
};
